/**
 * TransferOwnershipModal — Tier 0 slice-local component (SCRUM-172).
 *
 * Transfers the property from the current owner to another org member.
 * Mirrors the `UnitStatusModal` dialog contract. Fields match
 * `transferOwnershipSchema`: `fromUserId` (the current owner, fixed) +
 * `toUserId` (picker over org members, excluding the current owner) +
 * optional `transferredAt`. Sends the current ownership `version` as
 * the required If-Match (handled by the mutation hook).
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import { FilterSel } from './FilterSel.js';
import { dateOnlyToIso } from './ownershipDates.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useTransferOwnership } from '../api/use-ownership-mutations.js';
import type { OrgUserOption } from '../api/use-org-users.js';
import type { OwnershipView } from '../api/types.js';

export interface TransferOwnershipModalProps {
  readonly propertyId: string;
  readonly ownership: OwnershipView;
  readonly members: readonly OrgUserOption[];
  readonly onClose: () => void;
}

export function TransferOwnershipModal({
  propertyId,
  ownership,
  members,
  onClose,
}: TransferOwnershipModalProps): React.ReactElement {
  const [toUserId, setToUserId] = React.useState('');
  const [transferredAt, setTransferredAt] = React.useState('');
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { mutateAsync, status, errorMessage, reset } = useTransferOwnership();
  const sr = useSrAnnouncement();

  useFocusTrap(dialogRef, { initialFocusRef: cancelRef });

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const isValid = toUserId.length > 0;

  const handleSave = async (): Promise<void> => {
    if (!isValid) return;
    const iso = dateOnlyToIso(transferredAt);
    try {
      await mutateAsync({
        propertyId,
        currentVersion: ownership.version,
        body: {
          fromUserId: ownership.userId,
          toUserId,
          ...(iso !== undefined ? { transferredAt: iso } : {}),
        },
      });
      sr.polite('Ownership transferred');
      onClose();
    } catch {
      // errorMessage drives the inline role="alert"; modal stays open.
    }
  };

  // Exclude the current owner from the transfer target list.
  const targetOptions = [
    { id: '', label: 'Select the new owner…' },
    ...members
      .filter((m) => m.id !== ownership.userId)
      .map((m) => ({ id: m.id, label: m.name })),
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {sr.regions}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transfer-ownership-title"
        aria-describedby="transfer-ownership-subtitle"
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-xl)' }}
      >
        <h2
          id="transfer-ownership-title"
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Transfer ownership
        </h2>
        <p
          id="transfer-ownership-subtitle"
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Move ownership from {ownership.ownerName} to another member.
          The previous record is closed and preserved in history.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span
              className="text-xs font-bold uppercase"
              style={{ color: 'var(--fg-tertiary)', letterSpacing: '0.05em' }}
            >
              New owner
            </span>
            <FilterSel<string>
              label="New owner"
              value={toUserId}
              options={targetOptions}
              onChange={(next) => {
                if (errorMessage !== null) reset();
                setToUserId(next);
              }}
            />
          </div>

          <TextField
            label="Transfer date (optional)"
            type="date"
            value={transferredAt}
            onChange={(e) => {
              if (errorMessage !== null) reset();
              setTransferredAt(e.target.value);
            }}
          />
        </div>

        {errorMessage !== null ? (
          <div
            role="alert"
            className="mt-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={!isValid || status === 'pending'}
          >
            {status === 'pending' ? 'Transferring…' : 'Transfer ownership'}
          </Button>
        </div>
      </div>
    </div>
  );
}
